import {
  type EditableRecords,
  preferencesErrorMessage,
  saveDisplayPreferences,
  saveGoalEdits,
  savePhaseEdits,
  saveProfileEdits,
} from '~/services/settingsService'
import type { SolaDatabase } from '~/storage/db'
import { GoalSection, PhaseSection, PreferencesSection, ProfileSection } from './SetupSections'

/**
 * The editing half of Settings.
 *
 * Separate from `SettingsPage` because backup and restore have nothing to do
 * with editing setup, and keeping both in one component pushed it past the
 * complexity budget — which is refactored rather than suppressed (D-056).
 *
 * Each section reports a failure by returning a message and reports success by
 * returning nothing. `onSaved` therefore runs **only** after a write actually
 * succeeded: a failed write must never be reported as success.
 */

/**
 * Which of the four sections to render (D-099).
 *
 * They were one block until Settings was regrouped, and they belong in two
 * different groups: the phase and the goal are things the user is working
 * towards, while their height, date of birth and display units are not. Rather
 * than splitting the component — its four sections share the same save plumbing
 * and the same  — the caller says which subset it wants.
 *
 * Omitting the prop renders all four, so nothing that used it before had to
 * change.
 */
export type SetupSectionName = 'profile' | 'phase' | 'goal' | 'preferences'

interface SetupEditorProps {
  db: SolaDatabase
  records: EditableRecords
  onSaved: () => void
  sections?: readonly SetupSectionName[]
}

export function SetupEditor({ db, records, onSaved, sections }: SetupEditorProps) {
  const { profile, phase, goal, settings } = records
  const shows = (name: SetupSectionName) => sections === undefined || sections.includes(name)

  return (
    <>
      {profile === undefined || !shows('profile') ? null : (
        <ProfileSection
          profile={profile}
          lengthUnit={settings.lengthDisplayUnit}
          onSave={async (input) => {
            const written = await saveProfileEdits(db, input, new Date())
            if (!written.ok) return written.error.message
            onSaved()
            return undefined
          }}
        />
      )}

      {phase === undefined || !shows('phase') ? null : (
        <PhaseSection
          phase={phase}
          onSave={async (input) => {
            const written = await savePhaseEdits(db, phase, input, new Date())
            if (!written.ok) return written.error.message
            onSaved()
            return undefined
          }}
        />
      )}

      {goal === undefined || !shows('goal') ? null : (
        <GoalSection
          goal={goal}
          weightUnit={settings.weightDisplayUnit}
          lengthUnit={settings.lengthDisplayUnit}
          onSave={async (input) => {
            const written = await saveGoalEdits(db, goal, input, new Date())
            if (!written.ok) return written.error.message
            onSaved()
            return undefined
          }}
        />
      )}

      {!shows('preferences') ? null : (
        <PreferencesSection
          settings={settings}
          onSave={async (input) => {
            const written = await saveDisplayPreferences(db, input, new Date())
            if (!written.ok) return preferencesErrorMessage(written.error)
            onSaved()
            return undefined
          }}
        />
      )}
    </>
  )
}
